// needs to support both client and server because quotes slider is server component and quotes grid is client component

import React from "react";
import styles from "./QuoatesCard.module.scss";
import {
  ZitateGridOptionsType,
  ZitateSliderOptionsType,
} from "@/types/ModuleOptionsType";
import { customSanitize } from "../../utils/customSanitize";
import Image from "next/image";
import { UserImage } from "./UserImage/UserImage";
import { QuotesElementType } from "@/modules/QuoteSliderModule/QuoteSliderModule";
import { StarIcon } from "./StarIcon";
import { GetIcon, GetLabel } from "@/getData/fetchData.server";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG.client";

export type QuoatesCardOptions =
  | ZitateSliderOptionsType
  | ZitateGridOptionsType;

interface QuoatesCardProps {
  quotesOptions: QuoatesCardOptions;
  element: QuotesElementType;
  showAvatar?: boolean;
  showRating?: boolean;
  cardType: "slider" | "grid";
  headling_level: number;
  skeleton?: boolean;
  getIcon: GetIcon;
  getLabel: GetLabel;
  moduleWidth?: string;
}

export const QuoatesCard = ({
  element,
  quotesOptions,
  showAvatar = false,
  showRating = false,
  cardType,
  headling_level,
  skeleton = false,
  getIcon,
  getLabel,
  moduleWidth,
}: QuoatesCardProps) => {
  const {
    max_character,
    box: { show_border_radius, show_box_shadow, show_card_box },
  } = quotesOptions;

  const skeletonElementClass = skeleton ? "skeleton-element" : "";

  const {
    title,
    content,
    firma,
    rating_stars: ratingStars,
    name,
    position,
    source,
    image,
    preview_image: previewImage,
    text_size: textSize,
  } = element;
  const maxChar = Object.hasOwn(max_character, "small_min_characters")
    ? max_character.small_min_characters || 0
    : 0;
  const textClass = content.length >= maxChar ? "" : `quotes-${cardType}-big`;
  const hasSource =
    (source?.type === "logo" && source.logo) ||
    (source?.type === "text" && source.text);
  const hasRating = ratingStars > 0 && showRating;

  const QuotesJSX = (
    <>
      <span className={`quotes-name is-block ${skeletonElementClass}`}>
        {name}
      </span>
      <span
        className={`quotes-source-position is-block ${skeletonElementClass}`}
      >
        {position}
        {position && firma && ", "}
        {firma}
      </span>
    </>
  );

  const gridAvatarImage = image || getIcon("avatar");
  const hasGridAvatar = !!gridAvatarImage && showAvatar;
  const sliderAvatarImage = showAvatar && image ? image : false;
  const hasPreviewImage = cardType === "slider" && !!previewImage;
  const sliderTextClass = textSize === "big" ? "quotes-slider-big" : "";
  const cardBoxClass =
    cardType === "slider"
      ? show_card_box
        ? styles["has-card-box"]
        : ""
      : show_card_box
        ? "is-box"
        : "";
  const cardRadiusClass =
    show_border_radius && show_card_box
      ? cardType === "slider"
        ? styles["has-card-radius"]
        : "has-border-radius"
      : "";
  const cardShadowClass =
    show_box_shadow && show_card_box
      ? cardType === "slider"
        ? styles["has-card-shadow"]
        : "has-box-shadow"
      : "";
  const quoteIconUrl = getIcon("quote");

  const isNarrowFrame = moduleWidth === "narrow-frame";
  const leftColumnSize = hasPreviewImage
    ? "is-5-tablet is-6-desktop"
    : `is-4-tablet ${isNarrowFrame ? "" : "is-3-fullhd"}`;
  const rightColumnSize = hasPreviewImage
    ? "is-7-tablet is-6-desktop"
    : `is-8-tablet ${isNarrowFrame ? "" : "is-9-fullhd"}`;

  const SliderCardJSX = (
    <div className={`columns is-multiline is-align-items-flex-end`}>
      <div
        className={`column  ${leftColumnSize} ${styles["slider-profile-column"]}`}
      >
        <div
          className={`${styles["slider-person-card"]} ${hasPreviewImage ? "" : "is-box"}`}
        >
          <div className={styles["slider-person-info"]}>
            <div className={styles["slider-name-row"]}>
              {sliderAvatarImage && (
                <UserImage
                  data-card-type="slider-inline"
                  image={sliderAvatarImage}
                />
              )}
              <span className={`quotes-name is-block ${skeletonElementClass}`}>
                {name}
              </span>
            </div>
            <span className={styles["slider-person-divider"]} aria-hidden />
            <span
              className={`quotes-source-position is-block ${skeletonElementClass}`}
            >
              {
                <span
                  className="is-block"
                  dangerouslySetInnerHTML={{
                    __html: customSanitize(
                      `${position} ${position && firma && ","}`,
                    ),
                  }}
                />
              }

              {
                <span
                  className="is-block"
                  dangerouslySetInnerHTML={{ __html: customSanitize(firma) }}
                />
              }
            </span>
          </div>

          {(hasSource || hasRating) && (
            <div className={styles["slider-source-rating"]}>
              {hasSource && <SourceDisplay source={source} />}
              {hasRating && (
                <Stars rating={ratingStars} getLabel={getLabel} showValue />
              )}
            </div>
          )}
        </div>

        {previewImage && (
          <div className={styles["preview-image"]}>
            <Image
              src={previewImage.url}
              alt={previewImage.alt}
              fill
              sizes="(min-width: 769px) 50vw, 100vw"
              quality={90}
            />
          </div>
        )}
      </div>

      <div className={`column  ${rightColumnSize} ${styles["slider-quote"]}`}>
        {quoteIconUrl && (
          <CustomSVG
            src={quoteIconUrl}
            className={`${styles["quote-mark"]} quote-mark`}
            wrapper="span"
            width={64}
            height={46}
            aria-hidden="true"
          />
        )}
        {title && (
          <p role="heading" aria-level={headling_level} className="title">
            {title}
          </p>
        )}
        <blockquote
          className={`p ${sliderTextClass} ${skeletonElementClass} pb-0`}
          dangerouslySetInnerHTML={{ __html: customSanitize(content) }}
        />
      </div>
    </div>
  );

  return (
    <article
      data-card-type={cardType}
      className={`${styles["quote-card"]} ${cardType === "slider" ? (hasPreviewImage ? styles["has-preview-image"] : styles["without-preview-image"]) : styles["no-image"]} quote-card ${cardBoxClass} ${cardRadiusClass} ${cardShadowClass} ${hasRating ? `${styles["has-rating"]}` : ""} ${hasSource ? styles["has-source"] : ""}`}
    >
      {cardType === "slider" ? (
        SliderCardJSX
      ) : (
        <div className="h-100 is-flex is-flex-direction-column">
          <header>
            <div className="is-flex is-justify-content-space-between is-align-items-center">
              {hasRating && (
                <Stars
                  rating={ratingStars}
                  extraClass="shrink-0"
                  getLabel={getLabel}
                />
              )}

              <SourceDisplay
                source={source}
                extraClass={`shrink-0 ${skeletonElementClass}`}
              />
            </div>
          </header>

          <div className={styles["quote-wrapper"]}>
            <section className={styles["text-wrapper"]}>
              <p role="heading" aria-level={headling_level} className={"title"}>
                {title}
              </p>
              <blockquote
                className={`p ${textClass ? textClass : ""} ${skeletonElementClass}`}
                dangerouslySetInnerHTML={{ __html: customSanitize(content) }}
              />
            </section>

            <footer className={styles["user-info"]}>
              {hasGridAvatar && gridAvatarImage && (
                <UserImage data-card-type={cardType} image={gridAvatarImage} />
              )}

              <div>{QuotesJSX}</div>
            </footer>
          </div>
        </div>
      )}
    </article>
  );
};

const SourceLogo: React.FC<{ logo: QuotesElementType["source"]["logo"] }> = ({
  logo,
}) => {
  if (!logo) return null;
  return (
    <div className={styles["source-logo"]}>
      <Image
        src={logo.url}
        alt={logo.alt}
        width={logo.width}
        height={logo.height}
        style={{ objectFit: "cover" }}
        sizes="25vw"
        quality={90}
        // blurDataURL={`data:${logo.mime_type}${logo.image_blur}`}
        // placeholder="blur"
      />
    </div>
  );
};

const SourceText: React.FC<{
  text: QuotesElementType["source"]["text"];
}> = ({ text }) => <div className={"quotes-source-position"}>{text}</div>;

const SourceDisplay: React.FC<{
  source: QuotesElementType["source"];
  extraClass?: string;
}> = ({ source, extraClass = "" }) => {
  if (
    (source.type === "logo" && !source.logo) ||
    (source.type === "text" && !source.text)
  )
    return null;
  return (
    <div className={`${styles.source} ${extraClass}`}>
      {source.type === "logo" && source.logo && (
        <SourceLogo logo={source.logo} />
      )}
      {source.type === "text" && source.text && (
        <SourceText text={source.text} />
      )}
    </div>
  );
};

interface StarsProps {
  rating: number;
  extraClass?: string;
  getLabel: GetLabel;
  showValue?: boolean;
}

const Stars = ({
  rating,
  extraClass = "",
  getLabel,
  showValue = false,
}: StarsProps) => {
  const fullStars = Math.floor(rating);
  const partialStarPercentage = (rating - fullStars) * 100;
  const emptyStars = 5 - Math.ceil(rating);
  const label = getLabel("rating_label").replace("$rating", rating.toString());

  return (
    <span
      role="img"
      aria-label={label}
      className={`${styles.rating} ${extraClass}`}
    >
      <span className={`is-flex ${styles["stars-wrapper"]}`} aria-hidden="true">
        {Array.from({ length: fullStars }, (_, i) => (
          <StarIcon
            key={`full-${i}`}
            fillPercentage={100}
            color={"var(--quotes-slider-stars-color)"}
          />
        ))}
        {partialStarPercentage > 0 && (
          <StarIcon
            fillPercentage={partialStarPercentage}
            color={"var(--quotes-slider-stars-color)"}
          />
        )}
        {Array.from({ length: emptyStars }, (_, i) => (
          <StarIcon
            key={`empty-${i}`}
            fillPercentage={0}
            color={"var(--quotes-slider-stars-color)"}
          />
        ))}
      </span>
      {showValue && (
        <span className={styles["rating-value"]} aria-hidden="true">
          {rating}/5
        </span>
      )}
    </span>
  );
};
